import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RequestStripePayoutsInterface } from '../../../interface/stripe-payouts.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  fetchUnapprovedPendingCount,
  fetchUnapprovedStripePayouts,
} from '../../../redux/slices/UnapprovedStripePayoutsSlice';
import MyButton from '../../../shared-components/button/Button';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import stripePayoutsApi from '../../../utils/api/stripe-payouts';

interface ModalBoxDetail {
  title: string;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

/**
 * Approved content component
 * @param {object} properties - The component properties
 * @param {any} properties.id - The id propclea
 * @param {any} properties.setModalBoxStatus - The setModalBoxStatus prop
 * @param {any} properties.setModalBoxDetail - The setModalBoxDetail prop
 * @returns {any} The rendered component
 */
function ApprovedContent(properties: {
  id: string;
  setModalBoxStatus: Dispatch<SetStateAction<'open' | 'close'>>;
  setModalBoxDetail: Dispatch<SetStateAction<ModalBoxDetail>>;
}) {
  const { id, setModalBoxStatus, setModalBoxDetail } = properties;
  const { t } = useTranslation('stripePayout');
  const dispatch = useAppDispatch();
  const [buttonStatus, setButtonStatus] = useState(false);
  const { limit, page, loading } = useAppSelector((state) => state.unapprovedStripePayouts);

  /**
   * Handle the fetch the data in api function
   * @param {any} data - The request data
   */
  const fetchData = (data: RequestStripePayoutsInterface) => {
    if (!loading) {
      dispatch(fetchUnapprovedStripePayouts(data));
    }
  };

  /**
   * Function for handling payouts approve
   */
  const handleApprovePayment = async () => {
    try {
      setButtonStatus(true);
      await stripePayoutsApi.approveStripePayouts(id);
      fetchData({ page, limit });
      dispatch(fetchUnapprovedPendingCount());
      setModalBoxDetail({
        title: '',
        content: (
          <SuccessNotification
            title={t('StripeApprovedSuccessfully')}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setModalBoxStatus('close');
              setButtonStatus(false); // Reset after modal closes
            }}
          />
        ),
        rightCorner: '',
        notificationModal: true,
      });

      setModalBoxStatus('open');
    } catch (error: any) {
      setButtonStatus(false); // Reset if error
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  return (
    <div className="content-warp">
      <div className="content">
        <h1>{t('approvePayoutTitle')}</h1>
        <p>
          <span>{t('warningLabel')}</span>
          <span>{t('approvePayoutMessage')}</span>
        </p>
      </div>
      <div className="content-button">
        <MyButton
          label={t('cancel')}
          buttonType="danger-outline"
          onClickFunc={() => setModalBoxStatus('close')}
          disableButton={buttonStatus}
        />
        <MyButton
          label={t('approve')}
          buttonType="submit"
          onClickFunc={() => handleApprovePayment()}
          loading={buttonStatus}
        />
      </div>
    </div>
  );
}

export default ApprovedContent;
